import { PageWrapper, Search } from "@/components";
import * as GlobalS from "@/components/styled";
import { GreenIcon, RedIcon } from "@/components/styled";
import { CONTENT_WIDTH } from "@/enums";
import { getOrderBy, nameof, routes } from "@/helpers";
import { getFilenameFromContentDisposition } from "@/helpers/getFileNameFromContentDisposition";
import { IProjectRole } from "@/interfaces";
import { IRegionalProjectRole } from "@/interfaces/Project/Roles/IRegionalProjectRole";
import {
  useDeleteProjectRoleMutation,
  useSearchProjectRolesQuery,
} from "@/services";
import { authorizationHeaders } from "@/services/helpers";
import { Pager } from "@/sharedStyled";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleCheck,
  faCirclePlus,
  faCircleXmark,
  faEdit,
  faFileExport,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Popconfirm, Row, Space, Switch, Table } from "antd";
import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrayParam,
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
} from "use-query-params";

export const ProjectRolesList = () => {
  const navigate = useNavigate();
  const intl = useIntl();

  const [projectRoles, setProjectRoles] = useState<IProjectRole[]>();
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState<boolean>(false);

  const [queryParams, setQueryParams] = useQueryParams({
    page: NumberParam,
    name: StringParam,
    functionalArea: StringParam,
    limitedList: BooleanParam,
    projectType: ArrayParam,

    orderBy: ArrayParam,
    query: StringParam,
    includeInactive: BooleanParam,
    c: ArrayParam,
  });

  const [deleteProjectRole] = useDeleteProjectRoleMutation();
  const {
    data: { data: searchResults, totalCount } = {},
    isError: isSearchProjectRolesError,
    isFetching: fetchingProjectRoles,
  } = useSearchProjectRolesQuery(queryParams);

  useEffect(() => {
    setProjectRoles(searchResults);
  }, [searchResults]);

  useEffect(() => {
    if (!isSearchProjectRolesError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isSearchProjectRolesError]);

  const handleChange = (
    pagination: unknown,
    filters: unknown,
    sorter: unknown
  ): void => {
    handleSort(sorter);
  };

  const toggleShowInactive = (checked: boolean): void => {
    setShowInactive(checked);
    setQueryParams({ includeInactive: checked });
  };

  const handleSort = (sorter: unknown): void => {
    setQueryParams(getOrderBy(sorter));
  };

  const hasLimitedList = (
    regionalProjectRoles: IRegionalProjectRole[] | undefined
  ): boolean | undefined =>
    regionalProjectRoles?.some((role) => role.hasLimitedList === true);

  const hasDefaultUsers = (
    regionalProjectRoles: IRegionalProjectRole[] | undefined
  ): boolean | undefined =>
    regionalProjectRoles?.some((role) => role.hasDefaultUsers === true);

  const columns = [
    {
      title: intl.formatMessage({ id: "projectRoles.colName" }),
      dataIndex: nameof<IProjectRole>("name"),
      key: "nameSort",
      className: "ant-table-column-has-filter",
      sorter: true,
      colConfigurator: {
        required: true,
        noFormProperty: true,
      },
    },
    {
      title: intl.formatMessage({ id: "projectRoles.colLimitedList" }),
      render: ({ regionalProjectRoles }: IProjectRole) =>
        hasLimitedList(regionalProjectRoles) ? (
          <GreenIcon icon={faCircleCheck as IconProp} />
        ) : (
          <RedIcon icon={faCircleXmark as IconProp} />
        ),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.colDefaultUsers" }),
      render: ({ regionalProjectRoles }: IProjectRole) =>
        hasDefaultUsers(regionalProjectRoles) ? (
          <GreenIcon icon={faCircleCheck as IconProp} />
        ) : (
          <RedIcon icon={faCircleXmark as IconProp} />
        ),
    },
    {
      title: intl.formatMessage({ id: "projectRoles.colIsActive" }),
      render: ({ isActive }: IProjectRole) =>
        isActive ? (
          <GreenIcon icon={faCircleCheck as IconProp} />
        ) : (
          <RedIcon icon={faCircleXmark as IconProp} />
        ),
    },
    {
      title: intl.formatMessage({ id: "general.actions" }),
      render: ({ id }: { id: string }) => (
        <Row>
          <Col>
            <Link to={routes.admin.projectRoles.get(id)}>
              <Button
                type="link"
                data-test-id={`project-roles-edit-${id}`}
                icon={<FontAwesomeIcon icon={faEdit as IconProp} />}
              />
            </Link>
          </Col>
          <Col>
            <Popconfirm
              title={intl.formatMessage({
                id: "projectRoles.actionDelete",
              })}
              okText={intl.formatMessage({ id: "general.delete" })}
              cancelText={intl.formatMessage({ id: "general.cancel" })}
              onConfirm={(): void => handleDelete(id)}
            >
              <Button
                type="link"
                data-test-id={`project-roles-delete-${id}`}
                icon={<FontAwesomeIcon icon={faTrash as IconProp} />}
              />
            </Popconfirm>
          </Col>
        </Row>
      ),
    },
  ];

  const handleDelete = (id: string): void => {
    deleteProjectRole(id)
      .unwrap()
      .then(() => {
        setProjectRoles(
          projectRoles?.filter((role: IProjectRole) => role.id !== id)
        );
        toast.success(
          intl.formatMessage(
            { id: "editForm.fieldDeleted" },
            {
              field: intl.formatMessage({ id: "projectRoles.projectRole" }),
            }
          )
        );
      });
  };

  const clearAll = useCallback(() => {
    setQueryParams({}, "push");
  }, [setQueryParams]);

  const handleExport = async () => {
    setIsExporting(true);
    const headers = await authorizationHeaders(new Headers());
    const response = await fetch("api/v1/admin/project-roles/export", {
      headers,
    });
    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    const fileName = contentDisposition
      ? getFilenameFromContentDisposition(contentDisposition)
      : "project-roles.xlsx";
    saveAs(blob, fileName);
    setIsExporting(false);
  };

  return (
    <>
      <PageWrapper pageTitle={intl.formatMessage({ id: "projectRoles.title" })}>
        <Row className="pb-2">
          <Col
            xxl={CONTENT_WIDTH.oneThird}
            lg={CONTENT_WIDTH.half}
            md={CONTENT_WIDTH.twoThirds}
            xs={CONTENT_WIDTH.max}
          >
            <Search
              queryParams={queryParams}
              setQueryParams={setQueryParams}
              loadingSearchResults={fetchingProjectRoles}
              clearAll={clearAll}
              searchTooltip={intl.formatMessage({
                id: "projectRoles.searchTooltip",
              })}
              searchPlaceholder={intl.formatMessage({
                id: "projectRoles.searchPlaceholder",
              })}
              parentComponentName="project-roles"
            />
          </Col>
          <Col className="pl-1 pt-1">
            <Switch
              data-test-id="show-inactive-switch"
              style={{ minWidth: "100px" }}
              checked={showInactive}
              onChange={(value) => toggleShowInactive(value)}
              checkedChildren={intl.formatMessage({
                id: "projectRoles.showInactiveRoles",
              })}
              unCheckedChildren={intl.formatMessage({
                id: "projectRoles.hideInactiveRoles",
              })}
            />
          </Col>
        </Row>
        <Row>
          <Col span={CONTENT_WIDTH.max}>
            <GlobalS.PageContentCard
              title={intl.formatMessage({ id: "projectRoles.tableTitle" })}
              extra={
                <Space>
                  <Button
                    data-test-id="export-xlsx-btn"
                    onClick={handleExport}
                    icon={<FontAwesomeIcon icon={faFileExport as IconProp} />}
                    loading={isExporting}
                  >
                    {intl.formatMessage({ id: "general.export" })}
                  </Button>
                  <Button
                    data-test-id="project-roles-add"
                    type="primary"
                    icon={<FontAwesomeIcon icon={faCirclePlus as IconProp} />}
                    onClick={(): void =>
                      navigate(routes.admin.projectRoles.add)
                    }
                  >
                    {intl.formatMessage({ id: "general.add" })}
                  </Button>
                </Space>
              }
            >
              <Table
                data-test-id="project-roles-table"
                columns={columns}
                dataSource={projectRoles}
                rowKey={({ id }: IProjectRole): string => id}
                pagination={false}
                loading={fetchingProjectRoles}
                scroll={{ x: true }}
                onChange={handleChange}
              />
              <Pager
                defaultCurrent={queryParams.page || 1}
                current={queryParams.page || 1}
                pageSize={20}
                total={totalCount}
                showSizeChanger={false}
                onChange={(page: number): void => setQueryParams({ page })}
                data-test-id="project-roles-table-pagination"
              />
            </GlobalS.PageContentCard>
          </Col>
        </Row>
      </PageWrapper>
    </>
  );
};
